// Customer Support Ticketing System

export type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketCategory = 'booking_issue' | 'payment_problem' | 'flight_change' | 'refund_request' | 'visa_query' | 'general_enquiry' | 'technical_issue' | 'complaint';

export interface TicketMessage {
  id: string;
  authorName: string;
  authorRole: 'customer' | 'agent' | 'system';
  content: string;
  timestamp: string;
  isInternal?: boolean;
}

export interface SupportTicket {
  id: string;
  ticketNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  bookingRef?: string;
  assignedAgent?: string;
  messages: TicketMessage[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  tags?: string[];
}

const TICKETS_KEY = 'dff_support_tickets';

export function generateTicketNumber(): string {
  return 'TKT-' + (Math.floor(Math.random() * 90000) + 10000);
}

export function createTicket(data: {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority?: TicketPriority;
  bookingRef?: string;
}): SupportTicket {
  const now = new Date().toISOString();
  const ticketNumber = generateTicketNumber();
  const ticket: SupportTicket = {
    id: `ticket_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    ticketNumber,
    customerName: data.customerName,
    customerEmail: data.customerEmail,
    customerPhone: data.customerPhone,
    subject: data.subject,
    description: data.description,
    category: data.category,
    priority: data.priority || 'medium',
    status: 'open',
    bookingRef: data.bookingRef,
    messages: [
      { id: `msg_${Date.now()}`, authorName: data.customerName, authorRole: 'customer', content: data.description, timestamp: now },
      { id: `msg_sys_${Date.now() + 1}`, authorName: 'System', authorRole: 'system', content: `Ticket ${ticketNumber} created. Our team will respond within 2 hours.`, timestamp: now },
    ],
    createdAt: now,
    updatedAt: now,
    tags: [],
  };
  saveTicket(ticket);
  return ticket;
}

export function saveTicket(ticket: SupportTicket): void {
  if (typeof window === 'undefined') return;
  const tickets = getAllTickets();
  const idx = tickets.findIndex(t => t.id === ticket.id);
  if (idx >= 0) {
    tickets[idx] = { ...ticket, updatedAt: new Date().toISOString() };
  } else {
    tickets.unshift(ticket);
  }
  localStorage.setItem(TICKETS_KEY, JSON.stringify(tickets));
}

export function getAllTickets(): SupportTicket[] {
  if (typeof window === 'undefined') return [];
  return JSON.parse(localStorage.getItem(TICKETS_KEY) || '[]');
}

export function getTicketById(id: string): SupportTicket | null {
  return getAllTickets().find(t => t.id === id) || null;
}

export function getTicketByNumber(ticketNumber: string): SupportTicket | null {
  return getAllTickets().find(t => t.ticketNumber === ticketNumber) || null;
}

export function updateTicketStatus(ticketId: string, status: TicketStatus, agentName?: string): SupportTicket | null {
  const ticket = getTicketById(ticketId);
  if (!ticket) return null;
  const updated: SupportTicket = {
    ...ticket,
    status,
    assignedAgent: agentName || ticket.assignedAgent,
    updatedAt: new Date().toISOString(),
    resolvedAt: status === 'resolved' ? new Date().toISOString() : ticket.resolvedAt,
    messages: [...ticket.messages, { id: `msg_${Date.now()}`, authorName: agentName || 'System', authorRole: 'system', content: `Ticket status updated to: ${status.replace('_', ' ').toUpperCase()}`, timestamp: new Date().toISOString() }],
  };
  saveTicket(updated);
  return updated;
}

export function addMessageToTicket(ticketId: string, content: string, authorName: string, authorRole: TicketMessage['authorRole'], isInternal = false): SupportTicket | null {
  const ticket = getTicketById(ticketId);
  if (!ticket) return null;
  const message: TicketMessage = { id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`, authorName, authorRole, content, timestamp: new Date().toISOString(), isInternal };
  const updated: SupportTicket = {
    ...ticket,
    messages: [...ticket.messages, message],
    status: authorRole === 'agent' ? 'waiting_customer' : ticket.status,
    updatedAt: new Date().toISOString(),
  };
  saveTicket(updated);
  return updated;
}

export function getTicketStats() {
  const tickets = getAllTickets();
  return {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    waitingCustomer: tickets.filter(t => t.status === 'waiting_customer').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    urgent: tickets.filter(t => t.priority === 'urgent').length,
    high: tickets.filter(t => t.priority === 'high').length,
  };
}

export function seedDemoTickets(): void {
  if (typeof window === 'undefined') return;
  const existing = getAllTickets();
  if (existing.length > 0) return;
  const demos = [
    { customerName: 'Ahmed Khan', customerEmail: 'ahmed.khan@email.com', customerPhone: '07700900123', subject: 'Flight date change request', description: 'I need to change my flight from 15th March to 20th March. Booking ref DFF123456.', category: 'flight_change' as TicketCategory, priority: 'high' as TicketPriority, bookingRef: 'DFF123456' },
    { customerName: 'Sarah Mitchell', customerEmail: 'sarah.m@email.com', subject: 'Refund not received', description: 'I cancelled my booking 2 weeks ago but have not received my refund yet.', category: 'refund_request' as TicketCategory, priority: 'urgent' as TicketPriority, bookingRef: 'DFF789012' },
    { customerName: 'Mohammed Rashid', customerEmail: 'mo.rashid@email.com', customerPhone: '07800123456', subject: 'Visa requirements for Dubai', description: 'Can you help me understand the visa requirements for travelling to Dubai from UK?', category: 'visa_query' as TicketCategory, priority: 'medium' as TicketPriority },
    { customerName: 'Priya Sharma', customerEmail: 'priya.s@email.com', subject: 'Payment failed but money deducted', description: 'My payment failed during booking but the money was deducted from my account.', category: 'payment_problem' as TicketCategory, priority: 'urgent' as TicketPriority },
  ];
  demos.forEach((d, i) => {
    const ticket = createTicket(d);
    if (i === 1) updateTicketStatus(ticket.id, 'in_progress', 'Agent Sarah');
    if (i === 2) updateTicketStatus(ticket.id, 'resolved', 'Agent James');
  });
}

export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  booking_issue: 'Booking Issue', payment_problem: 'Payment Problem', flight_change: 'Flight Change',
  refund_request: 'Refund Request', visa_query: 'Visa Query', general_enquiry: 'General Enquiry',
  technical_issue: 'Technical Issue', complaint: 'Complaint',
};

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open', in_progress: 'In Progress', waiting_customer: 'Waiting Customer', resolved: 'Resolved', closed: 'Closed',
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent',
};
